import { Camera, Matrix, Ray, Scene, Vector2, Vector3 } from 'babylonjs';

/**
 * Canvas-relative projection helpers (dpr-aware). web-core's `screenToRay`
 * assumes a fullscreen window (reads window.innerWidth); these work for any
 * canvas size, so the rect overlay and pointer math stay correct when embedded.
 *
 * CSS px ↔ render px conversion uses the engine's hardware scaling level
 * (= 1/devicePixelRatio when adaptToDeviceRatio is on).
 */

/** Project a world point to CSS pixels relative to the canvas top-left, or null
 *  if it is behind the camera. */
export function worldToCss(scene: Scene, camera: Camera, world: Vector3): Vector2 | null {
  // In a right-handed scene the camera looks down -Z, so points in front have
  // negative view-space z.
  const viewZ = Vector3.TransformCoordinates(world, camera.getViewMatrix()).z;
  if (viewZ >= 0) return null;

  const engine = scene.getEngine();
  const w = engine.getRenderWidth();
  const h = engine.getRenderHeight();
  const p = Vector3.Project(world, Matrix.Identity(), scene.getTransformMatrix(), camera.viewport.toGlobal(w, h));
  const scale = engine.getHardwareScalingLevel();
  return new Vector2(p.x * scale, p.y * scale);
}

/** Build a world-space ray from a CSS pixel position (canvas-relative). */
export function cssToRay(scene: Scene, camera: Camera, cssX: number, cssY: number): Ray {
  const engine = scene.getEngine();
  const w = engine.getRenderWidth();
  const h = engine.getRenderHeight();
  const scale = engine.getHardwareScalingLevel();
  const rx = cssX / scale;
  const ry = cssY / scale;
  const view = camera.getViewMatrix();
  const proj = camera.getProjectionMatrix();
  const near = Vector3.Unproject(new Vector3(rx, ry, 0), w, h, Matrix.Identity(), view, proj);
  const far = Vector3.Unproject(new Vector3(rx, ry, 1), w, h, Matrix.Identity(), view, proj);
  return new Ray(near, far.subtract(near).normalize());
}
